import dayjs from "dayjs";
import customParseFormat from "dayjs/plugin/customParseFormat";
import { COPY_FEEDBACK_MS } from '../constants/documentEditorConstants';
import { sanitizeSafeHtml } from '../components/common/SafeHtml';

// Enable custom format parsing (required for "h:mm A" time format)
dayjs.extend(customParseFormat);

// Format diarized transcripts
const formatDiarizedTranscript = (transcript) => {
  if (!transcript) return "";

  // Split by lines and process each line
  const lines = transcript.split("\n");
  const formattedLines = lines.map((line) => {
    // Match speaker patterns like "Doctor:", "Patient:", etc.
    const speakerMatch = line.match(/^(Doctor|Patient|Speaker \d+|[A-Za-z\s]+):\s*(.+)$/);

    if (speakerMatch) {
      const speaker = speakerMatch[1].trim();
      const text = speakerMatch[2].trim();

      // Format with speaker labels and proper spacing
      return `${speaker}: ${text}`;
    }

    // Return line as-is if it doesn't match speaker pattern
    return line;
  });

  // Join with double line breaks for better readability
  return formattedLines
    .filter((line) => line.trim()) // Remove empty lines
    .join("\n\n");
};

export const getPreferredTranscript = (documentData, settings) => {
  if (!documentData) return "";

  let transcript = "";

  if (settings?.transcriptType === "diarized") {
    // Prioritize diarized content when requested
    transcript =
      documentData.cleaned_text?.summary || // diarized
      documentData.processed_text?.summary || // fallback to processed
      documentData.text?.summary || // pre AI raw (nested)
      (typeof documentData.text === 'string' ? documentData.text : "") || // direct text storage
      "";
  } else {
    // Prioritize processed (non-diarized) content when requested
    transcript =
      documentData.processed_text?.summary || // processed but not diarized
      documentData.cleaned_text?.summary || // fallback to diarized
      documentData.text?.summary || // pre AI raw (nested)
      (typeof documentData.text === 'string' ? documentData.text : "") || // direct text storage
      "";
  }

  // Only format if we're requesting diarized content AND we have diarized content
  const shouldFormat = settings?.transcriptType === "diarized" && documentData.cleaned_text?.summary;

  return shouldFormat ? formatDiarizedTranscript(transcript) : transcript;
};
export const hasTranscriptData = (documentData, minLength = 0) => {
  if (!documentData) return false;

  // Check all possible locations for transcript/source text:
  // - cleaned_text.summary: diarized transcript from audio
  // - processed_text.summary: processed text (OCR, text paste, audio)
  // - text.summary: pre-AI raw transcript
  // - text (string): direct text storage (some OCR/upload paths)
  const transcript = 
    documentData.cleaned_text?.summary || 
    documentData.processed_text?.summary || 
    documentData.text?.summary ||
    (typeof documentData.text === 'string' ? documentData.text : null);
  
  if (!transcript) return false;
  
  // If minLength is specified, check transcript length
  if (minLength > 0) {
    return transcript.length >= minLength;
  }
  
  return true;
};

export const saveDocumentTitle = async (patientId, docId, title, documentData, setDocumentData, setIsEditingTitle, updateDataFunc) => {
  try {
    await updateDataFunc(`patients/${patientId}/documents`, docId, { title });
    setDocumentData(previous => ({ ...previous, title }));
    setIsEditingTitle(false);
  } catch (error) {
    console.error("Error updating title:", error);
  }
};

export const saveDocumentDate = async (patientId, docId, editableDate, documentData, setDocumentData, setIsEditingDate, updateDataFunc) => {
  const datePattern = /^(0[1-9]|1[0-2])[-/](0[1-9]|[12][0-9]|3[01])[-/](\d{4})$/;

  if (datePattern.test(editableDate)) {
    try {
      const selectedDate = editableDate;
      await updateDataFunc(`patients/${patientId}/documents`, docId, {
        documentDate: selectedDate,
        modifiedAt: new Date(selectedDate),
      });

      setDocumentData(previous => ({
        ...previous,
        documentDate: selectedDate,
      }));

      setIsEditingDate(false);
    } catch (error) {
      console.error("Error updating date:", error);
    }
  } else {
    alert("Invalid date format. Please use MM-DD-YYYY or MM/DD/YYYY for date.");
  }
};

export const saveDocumentTime = async (patientId, docId, field, timeValue, documentData, setDocumentData, setIsEditingTimeSpecific, updateDataFunc) => {
  const timePattern = /^(0?[1-9]|1[0-2]):([0-5]\d)\s?(AM|PM)$/i;

  if (!timePattern.test(timeValue)) {
    alert("Invalid time format. Please use HH:MM AM/PM for time.");

    return;
  }

  try {
    await updateDataFunc(`patients/${patientId}/documents`, docId, {
      [field]: timeValue,
    });

    setDocumentData(previous => ({
      ...previous,
      [field]: timeValue,
    }));

    setIsEditingTimeSpecific(false);
  } catch (error) {
    console.error("Error updating time:", error);
  }
};

export const saveTherapyTimeData = async (patientId, docId, field, value, documentData, setDocumentData, setIsEditingSpecific, updateDataFunc) => {
  try {
    // Remove old validation - now handled in Document.js
    // Validation is done before this function is called
    
    await updateDataFunc(`patients/${patientId}/documents`, docId, {
      [field]: value,
    });
    setDocumentData(previous => ({
      ...previous,
      [field]: value,
    }));

    setIsEditingSpecific(false);
  } catch (error) {
    console.error("Error updating therapy data:", error);
  }
};

export const copyToClipboardUtility = async (content, setCopiedState, isHtml = false) => {
  try {
    if (isHtml) {
      const safeContent = sanitizeSafeHtml(content);
      // Derive plain text fallback so paste into plain-text targets is clean
      const tempDiv = document.createElement("div");
      tempDiv.innerHTML = safeContent;
      const plainText = tempDiv.textContent || tempDiv.innerText || "";
      await navigator.clipboard.write([
        new ClipboardItem({
          "text/html": new Blob([safeContent], { type: "text/html" }),
          "text/plain": new Blob([plainText], { type: "text/plain" }),
        }),
      ]);
    } else {
      await navigator.clipboard.writeText(content);
    }
    setCopiedState(true);
    setTimeout(() => setCopiedState(false), COPY_FEEDBACK_MS);
  } catch (err) {
    console.error("Failed to copy: ", err);
  }
};

export const convertDateStringToDayjs = (dateString) => {
  if (!dateString) return null;
  return dayjs(dateString, "MM/DD/YYYY");
};

export const documentDatePickerPatch = (selectedDate, dateField = 'documentDate') => {
  if (!selectedDate || !selectedDate.isValid()) {
    throw new Error("Invalid date selected");
  }

  if (selectedDate.year() < 1900) {
    throw new Error("Year must be 1900 or later");
  }

  return {
    [dateField]: selectedDate.format("MM/DD/YYYY"),
    modifiedAt: new Date(selectedDate.toDate()),
  };
};

export const saveDocumentDateWithPicker = async (patientId, docId, selectedDate, documentData, setDocumentData, setIsEditingDate, updateDataFunc, dateField = 'documentDate') => {
  const patch = documentDatePickerPatch(selectedDate, dateField);
  await updateDataFunc(`patients/${patientId}/documents`, docId, patch);
  setDocumentData(previous => ({ ...previous, [dateField]: patch[dateField] }));
  setIsEditingDate(false);
};

export const convertTimeStringToDayjs = (timeString) => {
  if (!timeString) return null;
  return dayjs(timeString, "h:mm A");
};

export const documentTimePickerPatch = (field, selectedTime) => {
  if (!selectedTime || !selectedTime.isValid()) {
    throw new Error("Invalid time selected");
  }

  return { [field]: selectedTime.format("h:mm A") };
};

export const saveDocumentTimeWithPicker = async (patientId, docId, field, selectedTime, documentData, setDocumentData, setIsEditingTimeSpecific, updateDataFunc) => {
  const patch = documentTimePickerPatch(field, selectedTime);
  await updateDataFunc(`patients/${patientId}/documents`, docId, patch);
  setDocumentData(previous => ({ ...previous, [field]: patch[field] }));
  setIsEditingTimeSpecific(false);
};
